import { componentText, HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import { ifDefined } from 'lit/directives/if-defined.js'
// Side-effect import: the leading glyph below renders `<schmancy-icon>`, whose
// definition is that module's own `@customElement` call.
import '@schmancy/primitives/icons/icon'

@customElement('schmancy-breadcrumb')
export class SchmancyBreadcrumb extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
			nav {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				gap: 0.25rem;
			}
			.sep {
				color: var(--schmancy-sys-color-surface-onVariant, #79747e);
				user-select: none;
				padding: 0 0.25rem;
			}
			::slotted(schmancy-breadcrumb-item:last-of-type) {
				font-weight: 500;
			}
		`,
	]
	@property({ type: String }) separator = '/'

	render() {
		// Render separators between slotted items by manipulating after render.
		// The simpler approach uses the CSS adjacent-sibling pattern so the
		// separator is visually present without affecting the accessible name.
		return html`
			<nav role="navigation" aria-label=${this.ariaLabel || componentText('breadcrumb.landmark')}>
				<slot @slotchange=${() => this.insertSeparators()}></slot>
			</nav>
		`
	}

	private insertSeparators() {
		const slot = this.shadowRoot?.querySelector('slot') as HTMLSlotElement | null
		if (!slot) return
		// Exclude our own separator spans so they don't count as breadcrumb items.
		const items = slot.assignedElements({ flatten: true }).filter(el => !el.hasAttribute('data-schmancy-sep'))
		const existingSeps = [...this.querySelectorAll('[data-schmancy-sep]')]
		const neededCount = Math.max(0, items.length - 1)

		// Structural correctness check: count AND position must match.
		// Count-only fails when item identity/order changes at the same depth
		// (e.g. Drive/Invoices → Drive/Contracts — same item count, different
		// second item). After the consumer swaps children the separator span can
		// land in the wrong DOM position, but a count-only check would exit early
		// and leave it misplaced. Verify each non-last item has a sep span as
		// its immediate nextElementSibling; only skip the rebuild when both
		// count and every position are already correct.
		// This structural check is also the loop-breaker: slotchange fires as a
		// microtask AFTER insertSeparators returns, so the second call always
		// sees the fully rebuilt DOM and exits here without mutating.
		if (existingSeps.length === neededCount) {
			let alreadyCorrect = true
			for (let i = 0; i < items.length - 1; i++) {
				if (!items[i].nextElementSibling?.hasAttribute('data-schmancy-sep')) {
					alreadyCorrect = false
					break
				}
			}
			if (alreadyCorrect) return
		}

		existingSeps.forEach(el => el.remove())
		items.forEach((el, idx) => {
			if (idx === items.length - 1) return
			const sep = document.createElement('span')
			sep.setAttribute('data-schmancy-sep', '')
			sep.setAttribute('aria-hidden', 'true')
			sep.setAttribute('part', 'separator')
			sep.className = 'sep'
			sep.textContent = this.separator
			el.insertAdjacentElement('afterend', sep)
		})
	}
}

/**
 * Individual breadcrumb item, in the one form its own properties describe:
 *
 * - `href` — a link, because the crumb has a URL to point at.
 * - `interactive` — a BUTTON, for the crumb that is a step you can go back to
 *   whose destination is in-page state a URL cannot address (a wizard step, an
 *   undo trail, a drill chain). Rendered as a span such a crumb is dead text —
 *   visibly a crumb, silently inert, and invisible to a keyboard — so a trail
 *   that navigates in place opts in here rather than binding a click to a span.
 * - neither — a span, which is what a crumb with no destination has always
 *   been, and what `current` marks with `aria-current="page"`.
 *
 * The button carries no chrome of its own, so a trail that mixes links and
 * in-page steps reads as one trail. Activation is a plain `click` off the host
 * — a consumer already binding `@click` on the item needs nothing new, and the
 * keyboard comes with the element rather than with a handler.
 *
 * @element schmancy-breadcrumb-item
 * @slot - Label content.
 * @slot icon - A leading glyph before the label. Takes precedence over `icon`.
 * @attr href - If set, renders as an anchor.
 * @attr interactive - Renders the crumb as a button when it has no `href`.
 * @attr icon - Material Symbols name rendered as the leading glyph.
 * @attr current - Marks as `aria-current="page"`.
 */
@customElement('schmancy-breadcrumb-item')
export class SchmancyBreadcrumbItem extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-block;
				width: fit-content;
			}
			a,
			span,
			button {
				color: inherit;
				text-decoration: none;
			}
			a:hover,
			button:hover {
				text-decoration: underline;
			}
			/* The in-page crumb is a control, and a control that looks like a
			   control is a second visual vocabulary inside one trail. Preflight
			   already zeroes the border, margin and padding and hands a button the
			   inherited font and a transparent ground; these two are what it leaves
			   behind and what no utility spells — the UA's own appearance, and the
			   alignment a <button> computes as centred where the <a> and <span>
			   beside it compute start, which is invisible on a one-line crumb and
			   centres both lines of a crumb that wraps. Every other declaration on
			   this element is a utility on its class attribute. */
			button {
				appearance: none;
				text-align: inherit;
			}
		`,
	]
	@property({ type: String }) href = ''
	@property({ type: Boolean, reflect: true }) current = false

	/**
	 * Renders the crumb as a button when it has no `href` and is not `current`.
	 * Off by default: the span is what every crumb without a URL has rendered,
	 * and a primitive that promoted them all to controls would change the
	 * accessible tree of trails it has never seen.
	 */
	@property({ type: Boolean, reflect: true }) interactive = false

	/**
	 * A leading glyph is decorative: the label beside it already carries the
	 * crumb's name, so a second announcement of the same step is noise.
	 */
	@property({ type: String }) icon = ''

	@state() private hasIconSlot = false

	@queryAssignedElements({ slot: 'icon', flatten: true })
	private iconSlotContent!: HTMLElement[]

	render() {
		// Applied only when a glyph is actually present: an item with a bare
		// label keeps the plain inline box — and the wrapping it allows — that
		// every current consumer renders today.
		const shell = { 'inline-flex items-center gap-1': this.icon !== '' || this.hasIconSlot }

		// The glyph is decorative and the label beside it already names the step,
		// so the whole slot is hidden rather than only the fallback icon: a
		// consumer's own <img> or <svg> in this slot would otherwise join the
		// crumb's accessible name, which `<schmancy-icon>` alone knows to avoid.
		const leading = html`
			<span aria-hidden="true" class="contents">
				<slot
					name="icon"
					@slotchange=${() => {
						this.hasIconSlot = this.iconSlotContent.length > 0
					}}
				>
					${when(this.icon, () => html`<schmancy-icon class="text-base">${this.icon}</schmancy-icon>`)}
				</slot>
			</span>
		`

		if (this.href && !this.current) {
			return html`<a
				href=${this.href}
				aria-label=${ifDefined(this.ariaLabel || undefined)}
				class=${this.classMap(shell)}
				>${leading}<slot></slot
			></a>`
		}
		if (this.interactive && !this.current) {
			return html`<button
				type="button"
				aria-label=${ifDefined(this.ariaLabel || undefined)}
				class=${this.classMap({ ...shell, 'cursor-pointer': true })}
			>
				${leading}<slot></slot>
			</button>`
		}
		return html`<span aria-current=${this.current ? 'page' : 'false'} class=${this.classMap(shell)}
			>${leading}<slot></slot
		></span>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-breadcrumb': SchmancyBreadcrumb
		'schmancy-breadcrumb-item': SchmancyBreadcrumbItem
	}
}
